import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { styled } from '@mui/material/styles';

import { JobSelector, JobList, JobDetails, JobApplicationFrame } from './';
import { JobsStage } from '../JobsStage';
import {
  getJobList,
  SMAVA_BOARD_NAME,
  getLocationsFromJobList,
  getCategoriesFromJobList,
} from '../../../lib/greenHouse/greenHouseApi';

const GreenHouseIntegration = styled('div')`
  width: 100%;
`;

interface GreenHouseContainerProps {
  stageBackgroundImage?: string;
  showAnimation?: boolean;
}

/**
 * Uses router.query as state handler for selectedCategory, selectedLocation and selectedJob
 */
export const GreenHouseContainer: React.FC<GreenHouseContainerProps> = ({
  stageBackgroundImage,
  showAnimation,
}) => {
  const router = useRouter();
  const {
    gh_jid,
    selectedCategory,
    selectedLocation,
    showApplication,
    gh_src,
  } = router.query;

  const [jobList, setJobList] = useState([]);
  const [jobLocations, setJobLocations] = useState<string[]>([]);
  const [jobCategories, setJobCategories] = useState<string[]>([]);

  const scrollToRef = useRef<null | HTMLDivElement>(null);

  // Helpers for display of the different states
  const showJobList = (selectedCategory || selectedLocation) && !gh_jid;
  const showJobSelector = !gh_jid;
  const showJobDetails =
    gh_jid && (!showApplication || showApplication === 'false');
  const showJobApplicationFrame = showApplication === 'true' && gh_jid;

  useEffect(() => {
    // Create an scoped async function in the hook
    (async () => {
      setJobList(await getJobList(SMAVA_BOARD_NAME));
    })();
  }, []);

  useEffect(() => {
    setJobLocations(getLocationsFromJobList(jobList));
    setJobCategories(getCategoriesFromJobList(jobList));
  }, [jobList]);

  useEffect(() => {
    if (scrollToRef.current && showJobList) {
      scrollToRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [showJobList]);

  const handleJobSelectorSubmit = () => {
    const { query, pathname } = router;
    router.push(
      {
        pathname,
        query: { selectedLocation: 'all', selectedCategory: 'all', ...query },
      },
      undefined,
      { shallow: true },
    );
  };

  const handleJobSelection = (jobId: string) => {
    const { query, pathname } = router;
    router.push(
      {
        pathname,
        query: { ...query, gh_jid: jobId },
      },
      undefined,
      { shallow: true },
    );
  };

  const handleJobBacklink = () => {
    const { query, pathname } = router;
    router.push(
      { pathname, query: { ...query, gh_jid: null, showApplication: false } },
      undefined,
      {
        shallow: true,
      },
    );
  };

  const handleApplicationLink = () => {
    const { query, pathname } = router;

    router.push(
      { pathname, query: { ...query, showApplication: true } },
      undefined,
      {
        shallow: true,
      },
    );
  };

  return (
    <GreenHouseIntegration id="greenhouse-integration">
      {showJobSelector && (
        <JobsStage
          backgroundImage={stageBackgroundImage}
          showAnimation={showAnimation}
        >
          <div ref={scrollToRef}>
            <JobSelector
              selectedCategory={selectedCategory?.toString()}
              selectedLocation={selectedLocation?.toString()}
              categories={jobCategories}
              locations={jobLocations}
              handler={handleJobSelectorSubmit}
            />
          </div>
        </JobsStage>
      )}

      {showJobList && (
        <JobList
          selectedCategory={selectedCategory?.toString()}
          selectedLocation={selectedLocation?.toString()}
          jobs={jobList}
          handler={handleJobSelection}
        />
      )}

      {showJobDetails && (
        <JobDetails
          jobId={gh_jid?.toString()}
          backLinkHandler={handleJobBacklink}
          applicationLinkHandler={handleApplicationLink}
        />
      )}

      {showJobApplicationFrame && (
        <JobApplicationFrame
          boardName={SMAVA_BOARD_NAME}
          jobId={gh_jid.toString()}
          backlinkHandler={handleJobBacklink}
          source={gh_src?.toString()}
        />
      )}
    </GreenHouseIntegration>
  );
};
